// src/app/pages/app/HelpLoanPage.jsx
import { useMemo, useState } from "react";
import Modal from "../../components/common/Modal.jsx";
import Button from "../../components/common/Button.jsx";
import Input from "../../components/common/Input.jsx";
import {
  Home,
  DollarSign,
  Briefcase,
  Banknote,
  Upload,
  FileText,
  Shield,
  CheckCircle,
  Clock,
  Percent,
  TrendingUp,
  ArrowRight,
  ArrowLeft,
  Calculator,
  FileCheck,
  Lock,
  UserCheck,
  CreditCard,
  Calendar,
  Target,
  AlertCircle,
  XCircle,
} from "lucide-react";

import { useCreateHelpLoanApplication, usePresignHelpLoanUpload, useUploadToS3 } from "../../features/helpLoan/useHelpLoan.js";

function isPositiveNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function safeExtFromMime(mime) {
  if (!mime) return "FILE";
  const p = mime.split("/")[1];
  return (p || "FILE").toUpperCase();
}

export default function HelpLoanPage() {
  const steps = [
    { id: 0, title: "Property Details", icon: <Home className="w-4 h-4" /> },
    { id: 1, title: "Income Proof", icon: <Briefcase className="w-4 h-4" /> },
    { id: 2, title: "Bank Details", icon: <Banknote className="w-4 h-4" /> },
    { id: 3, title: "Review & Submit", icon: <FileCheck className="w-4 h-4" /> },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  const [error, setError] = useState("");

  // Property Details
  const [propertyDetails, setPropertyDetails] = useState({
    address: "",
    city: "",
    country: "",
    propertyType: "Residential",
    ownershipType: "Owned",
    estimatedValue: "",
    yearBuilt: "",
    squareFeet: "",
  });

  // Income Details
  const [incomeDetails, setIncomeDetails] = useState({
    employmentType: "Full-time",
    employerName: "",
    monthlyIncome: "",
    yearsEmployed: "",
    incomeFile: null,
    incomeFileKey: null,
    incomeFileUrl: null,
  });

  // Bank Details
  const [bankDetails, setBankDetails] = useState({
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "Checking",
    bankProofFile: null,
    bankProofFileKey: null,
    bankProofFileUrl: null,
  });

  // Loan Details
  const [loanDetails, setLoanDetails] = useState({
    loanAmount: "",
    loanTerm: "12",
    purpose: "Home Improvement",
  });

  const progress = ((currentStep + 1) / steps.length) * 100;

  const presign = usePresignHelpLoanUpload();
  const upload = useUploadToS3();
  const createApp = useCreateHelpLoanApplication();

  const isBusy = presign.isPending || upload.isPending || createApp.isPending;

  const incomeFilePreview = useMemo(() => {
    if (!incomeDetails.incomeFile) return null;
    return {
      name: incomeDetails.incomeFile.name,
      size: formatFileSize(incomeDetails.incomeFile.size),
      type: safeExtFromMime(incomeDetails.incomeFile.type),
      uploaded: !!incomeDetails.incomeFileKey,
    };
  }, [incomeDetails.incomeFile, incomeDetails.incomeFileKey]);

  const bankFilePreview = useMemo(() => {
    if (!bankDetails.bankProofFile) return null;
    return {
      name: bankDetails.bankProofFile.name,
      size: formatFileSize(bankDetails.bankProofFile.size),
      type: safeExtFromMime(bankDetails.bankProofFile.type),
      uploaded: !!bankDetails.bankProofFileKey,
    };
  }, [bankDetails.bankProofFile, bankDetails.bankProofFileKey]);

  const calculateLoanMetrics = () => {
    const amount = Number(loanDetails.loanAmount) || 0;
    const term = Number(loanDetails.loanTerm) || 12;
    const monthly = term > 0 ? amount / term : 0;
    const interestRate = 8.5;
    const totalInterest = amount * (interestRate / 100);
    const totalPayment = amount + totalInterest;

    return {
      monthlyPayment: monthly.toFixed(2),
      interestRate,
      totalInterest: totalInterest.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
    };
  };

  const validateStep = () => {
    setError("");

    switch (currentStep) {
      case 0:
        if (!propertyDetails.address.trim()) return "Property address is required";
        if (!propertyDetails.city.trim()) return "City is required";
        if (!propertyDetails.country.trim()) return "Country is required";
        if (!isPositiveNumber(propertyDetails.estimatedValue)) return "Valid property value is required";
        if (Number(propertyDetails.estimatedValue) < 50000) return "Property value must be at least $50,000";
        break;

      case 1:
        if (!incomeDetails.employerName.trim()) return "Employer name is required";
        if (!isPositiveNumber(incomeDetails.monthlyIncome)) return "Valid monthly income is required";
        if (!isPositiveNumber(incomeDetails.yearsEmployed)) return "Years employed is required";
        if (!incomeDetails.incomeFile) return "Proof of income document is required";
        if (!incomeDetails.incomeFileKey) return "Please upload the income document (wait for upload to finish).";
        break;

      case 2:
        if (!bankDetails.bankName.trim()) return "Bank name is required";
        if (!bankDetails.accountHolder.trim()) return "Account holder name is required";
        if (!bankDetails.accountNumber.trim()) return "Account number is required";
        if (!bankDetails.routingNumber.trim()) return "Routing number is required";
        if (!bankDetails.bankProofFile) return "Bank statement proof is required";
        if (!bankDetails.bankProofFileKey) return "Please upload the bank statement (wait for upload to finish).";
        break;

      case 3:
        if (!isPositiveNumber(loanDetails.loanAmount)) return "Valid loan amount is required";
        if (Number(loanDetails.loanAmount) > Number(propertyDetails.estimatedValue) * 0.8) {
          return "Loan amount cannot exceed 80% of property value";
        }
        break;

      default:
        break;
    }

    return true;
  };

  const handleNext = () => {
    const validation = validateStep();
    if (validation === true) setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    else setError(validation);
  };

  const handleBack = () => {
    setError("");
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  async function handleUploadIncome(file) {
    setError("");
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("Income proof file is too large (max 10MB).");
      return;
    }

    setIncomeDetails((p) => ({ ...p, incomeFile: file, incomeFileKey: null, incomeFileUrl: null }));

    const pres = await presign.mutateAsync({ fileName: file.name, mimeType: file.type || "application/octet-stream" });
    await upload.mutateAsync({ uploadUrl: pres.upload_url, file });

    setIncomeDetails((p) => ({
      ...p,
      incomeFileKey: pres.s3_key,
      incomeFileUrl: pres.file_url || null,
    }));
  }

  async function handleUploadBank(file) {
    setError("");
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("Bank statement file is too large (max 10MB).");
      return;
    }

    setBankDetails((p) => ({ ...p, bankProofFile: file, bankProofFileKey: null, bankProofFileUrl: null }));

    const pres = await presign.mutateAsync({ fileName: file.name, mimeType: file.type || "application/octet-stream" });
    await upload.mutateAsync({ uploadUrl: pres.upload_url, file });

    setBankDetails((p) => ({
      ...p,
      bankProofFileKey: pres.s3_key,
      bankProofFileUrl: pres.file_url || null,
    }));
  }

  const handleSubmit = async () => {
    const validation = validateStep();
    if (validation !== true) {
      setError(validation);
      return;
    }

    setError("");

    const payload = {
      propertyDetails: {
        ...propertyDetails,
        estimatedValue: Number(propertyDetails.estimatedValue),
        yearBuilt: propertyDetails.yearBuilt ? Number(propertyDetails.yearBuilt) : null,
        squareFeet: propertyDetails.squareFeet ? Number(propertyDetails.squareFeet) : null,
      },
      incomeDetails: {
        employmentType: incomeDetails.employmentType,
        employerName: incomeDetails.employerName,
        monthlyIncome: Number(incomeDetails.monthlyIncome),
        yearsEmployed: Number(incomeDetails.yearsEmployed),
        incomeFileKey: incomeDetails.incomeFileKey,
        incomeFileUrl: incomeDetails.incomeFileUrl,
      },
      bankDetails: {
        bankName: bankDetails.bankName,
        accountHolder: bankDetails.accountHolder,
        accountNumber: bankDetails.accountNumber,
        routingNumber: bankDetails.routingNumber,
        accountType: bankDetails.accountType,
        bankProofFileKey: bankDetails.bankProofFileKey,
        bankProofFileUrl: bankDetails.bankProofFileUrl,
      },
      loanDetails: {
        loanAmount: Number(loanDetails.loanAmount),
        loanTerm: Number(loanDetails.loanTerm),
        purpose: loanDetails.purpose,
      },
    };

    const res = await createApp.mutateAsync(payload);

    const appId = res?.applicationId || res?.id || res?.application_id;
    setApplicationId(appId || "—");
    setOpen(true);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setError("");

    setPropertyDetails({
      address: "",
      city: "",
      country: "",
      propertyType: "Residential",
      ownershipType: "Owned",
      estimatedValue: "",
      yearBuilt: "",
      squareFeet: "",
    });

    setIncomeDetails({
      employmentType: "Full-time",
      employerName: "",
      monthlyIncome: "",
      yearsEmployed: "",
      incomeFile: null,
      incomeFileKey: null,
      incomeFileUrl: null,
    });

    setBankDetails({
      bankName: "",
      accountHolder: "",
      accountNumber: "",
      routingNumber: "",
      accountType: "Checking",
      bankProofFile: null,
      bankProofFileKey: null,
      bankProofFileUrl: null,
    });

    setLoanDetails({
      loanAmount: "",
      loanTerm: "12",
      purpose: "Home Improvement",
    });
  };

  const loanMetrics = calculateLoanMetrics();

  const apiErr =
    createApp.error?.response?.data?.detail ||
    createApp.error?.message ||
    presign.error?.message ||
    upload.error?.message ||
    "";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-100">Help Loan</h1>
            <p className="text-gray-400 mt-1">Property-backed financing application</p>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-gray-100 font-medium">Application Progress</div>
            <div className="text-sm text-gray-400">
              Step {currentStep + 1} of {steps.length}
            </div>
          </div>

          <div className="space-y-4">
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${
                    index === currentStep
                      ? "bg-green-500/20 border border-green-500/30 text-green-400"
                      : index < currentStep
                      ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                      : "bg-gray-800/50 border border-gray-700 text-gray-400"
                  }`}
                >
                  <div
                    className={`p-1.5 rounded-md ${
                      index === currentStep ? "bg-green-500" : index < currentStep ? "bg-emerald-500" : "bg-gray-700"
                    }`}
                  >
                    {step.icon}
                  </div>
                  <span className="text-sm font-medium">{step.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
            {/* Errors */}
            {(error || apiErr) && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400">{error || apiErr}</p>
                </div>
              </div>
            )}

            {/* Step 0 */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <Home className="w-6 h-6 text-green-400" />
                  <h2 className="text-xl font-bold text-gray-100">Property Details</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Property Address</label>
                    <Input
                      value={propertyDetails.address}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, address: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                    <Input
                      value={propertyDetails.city}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, city: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                    <Input
                      value={propertyDetails.country}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, country: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Property Type</label>
                    <select
                      value={propertyDetails.propertyType}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, propertyType: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option>Residential</option>
                      <option>Commercial</option>
                      <option>Industrial</option>
                      <option>Agricultural</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Ownership Type</label>
                    <select
                      value={propertyDetails.ownershipType}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, ownershipType: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option>Owned</option>
                      <option>Mortgaged</option>
                      <option>Leased</option>
                      <option>Inherited</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Estimated Value (USD)
                    </label>
                    <Input
                      value={propertyDetails.estimatedValue}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, estimatedValue: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Year Built
                    </label>
                    <Input
                      value={propertyDetails.yearBuilt}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, yearBuilt: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Square Feet</label>
                    <Input
                      value={propertyDetails.squareFeet}
                      onChange={(e) => setPropertyDetails({ ...propertyDetails, squareFeet: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1 */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-6 h-6 text-blue-400" />
                  <h2 className="text-xl font-bold text-gray-100">Income & Employment</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Employment Type</label>
                    <select
                      value={incomeDetails.employmentType}
                      onChange={(e) => setIncomeDetails({ ...incomeDetails, employmentType: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option>Full-time</option>
                      <option>Part-time</option>
                      <option>Self-employed</option>
                      <option>Contract</option>
                      <option>Retired</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Employer / Business Name</label>
                    <Input
                      value={incomeDetails.employerName}
                      onChange={(e) => setIncomeDetails({ ...incomeDetails, employerName: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Monthly Income (USD)
                    </label>
                    <Input
                      value={incomeDetails.monthlyIncome}
                      onChange={(e) => setIncomeDetails({ ...incomeDetails, monthlyIncome: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Years Employed
                    </label>
                    <Input
                      value={incomeDetails.yearsEmployed}
                      onChange={(e) => setIncomeDetails({ ...incomeDetails, yearsEmployed: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Upload className="inline w-4 h-4 mr-1" />
                      Proof of Income Document
                    </label>

                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center hover:border-emerald-500 transition">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="hidden"
                        id="income-file"
                        onChange={(e) => handleUploadIncome(e.target.files?.[0] || null)}
                        disabled={isBusy}
                      />
                      <label htmlFor="income-file" className={`cursor-pointer ${isBusy ? "opacity-60" : ""}`}>
                        <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-3">
                          <FileText className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-300 font-medium">
                          {upload.isPending ? "Uploading..." : "Upload Income Proof"}
                        </p>
                        <p className="text-gray-500 text-sm mt-1">PDF, JPG, PNG, DOC (Max 10MB)</p>
                      </label>
                    </div>

                    {incomeFilePreview && (
                      <div className="mt-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-green-400" />
                            <div>
                              <p className="text-gray-100 font-medium">{incomeFilePreview.name}</p>
                              <p className="text-gray-400 text-sm">
                                {incomeFilePreview.type} • {incomeFilePreview.size} •{" "}
                                {incomeFilePreview.uploaded ? (
                                  <span className="text-emerald-300">Uploaded</span>
                                ) : (
                                  <span className="text-amber-300">Not uploaded</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setIncomeDetails((p) => ({
                                ...p,
                                incomeFile: null,
                                incomeFileKey: null,
                                incomeFileUrl: null,
                              }))
                            }
                            className="text-gray-400 hover:text-red-400"
                            disabled={isBusy}
                            title="Remove"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <Banknote className="w-6 h-6 text-purple-400" />
                  <h2 className="text-xl font-bold text-gray-100">Bank Account Details</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Bank Name</label>
                    <Input
                      value={bankDetails.bankName}
                      onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Account Type</label>
                    <select
                      value={bankDetails.accountType}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountType: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option>Checking</option>
                      <option>Savings</option>
                      <option>Money Market</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Account Holder Name</label>
                    <Input
                      value={bankDetails.accountHolder}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountHolder: e.target.value })}
                      placeholder=""
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Account Number</label>
                    <Input
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                      placeholder=""
                      type="password"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Routing Number</label>
                    <Input
                      value={bankDetails.routingNumber}
                      onChange={(e) => setBankDetails({ ...bankDetails, routingNumber: e.target.value })}
                      placeholder=""
                      type="password"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Upload className="inline w-4 h-4 mr-1" />
                      Bank Statement Proof (Last 3 Months)
                    </label>

                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center hover:border-purple-500 transition">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        id="bank-file"
                        onChange={(e) => handleUploadBank(e.target.files?.[0] || null)}
                        disabled={isBusy}
                      />
                      <label htmlFor="bank-file" className={`cursor-pointer ${isBusy ? "opacity-60" : ""}`}>
                        <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-3">
                          <CreditCard className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-300 font-medium">{upload.isPending ? "Uploading..." : "Upload Bank Statement"}</p>
                        <p className="text-gray-500 text-sm mt-1">PDF, JPG, PNG (Max 10MB)</p>
                      </label>
                    </div>

                    {bankFilePreview && (
                      <div className="mt-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-purple-400" />
                            <div>
                              <p className="text-gray-100 font-medium">{bankFilePreview.name}</p>
                              <p className="text-gray-400 text-sm">
                                {bankFilePreview.type} • {bankFilePreview.size} •{" "}
                                {bankFilePreview.uploaded ? (
                                  <span className="text-emerald-300">Uploaded</span>
                                ) : (
                                  <span className="text-amber-300">Not uploaded</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setBankDetails((p) => ({
                                ...p,
                                bankProofFile: null,
                                bankProofFileKey: null,
                                bankProofFileUrl: null,
                              }))
                            }
                            className="text-gray-400 hover:text-red-400"
                            disabled={isBusy}
                            title="Remove"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calculator className="w-6 h-6 text-yellow-400" />
                  <h2 className="text-xl font-bold text-gray-100">Loan Details & Review</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Target className="inline w-4 h-4 mr-1" />
                      Loan Amount (USD)
                    </label>
                    <Input
                      value={loanDetails.loanAmount}
                      onChange={(e) => setLoanDetails({ ...loanDetails, loanAmount: e.target.value })}
                      placeholder=""
                      type="number"
                      className="bg-gray-800/50"
                      disabled={isBusy}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max: ${(Number(propertyDetails.estimatedValue || 0) * 0.8).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Loan Term (Months)
                    </label>
                    <select
                      value={loanDetails.loanTerm}
                      onChange={(e) => setLoanDetails({ ...loanDetails, loanTerm: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option value="6">6 Months</option>
                      <option value="12">12 Months</option>
                      <option value="24">24 Months</option>
                      <option value="36">36 Months</option>
                      <option value="60">60 Months</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Loan Purpose</label>
                    <select
                      value={loanDetails.purpose}
                      onChange={(e) => setLoanDetails({ ...loanDetails, purpose: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-100"
                      disabled={isBusy}
                    >
                      <option>Home Improvement</option>
                      <option>Debt Consolidation</option>
                      <option>Business Investment</option>
                      <option>Education</option>
                      <option>Medical Expenses</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-bold text-gray-100 mb-4">Loan Summary</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-sm text-gray-400">Monthly Payment</div>
                      <div className="text-2xl font-bold text-gray-100 mt-1">${loanMetrics.monthlyPayment}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-sm text-gray-400">Interest Rate</div>
                      <div className="text-2xl font-bold text-green-400 mt-1">{loanMetrics.interestRate}%</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-sm text-gray-400">Total Interest</div>
                      <div className="text-2xl font-bold text-yellow-400 mt-1">${loanMetrics.totalInterest}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-sm text-gray-400">Total Payment</div>
                      <div className="text-2xl font-bold text-blue-400 mt-1">${loanMetrics.totalPayment}</div>
                    </div>
                  </div>
                </div>

                {/* Review */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-100">Application Review</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-gray-400">Property Value</span>
                      <span className="text-gray-100 font-medium">
                        ${Number(propertyDetails.estimatedValue || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-gray-400">Monthly Income</span>
                      <span className="text-green-400 font-medium">
                        ${Number(incomeDetails.monthlyIncome || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-gray-400">Loan-to-Value Ratio</span>
                      <span className="text-yellow-400 font-medium">
                        {((Number(loanDetails.loanAmount || 0) / Number(propertyDetails.estimatedValue || 1)) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    By submitting, you confirm that documents uploaded are accurate and you agree to verification checks.
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="mt-8 pt-6 border-t border-gray-700/50 flex justify-between">
              <Button
                onClick={handleBack}
                disabled={currentStep === 0 || isBusy}
                className="border-gray-700 bg-gray-800/50 hover:bg-gray-800 text-gray-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              <div className="flex gap-3">
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={isBusy}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 border-transparent"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isBusy}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 border-transparent"
                  >
                    {isBusy ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Estimate */}
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-gray-100 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-yellow-400" />
              Quick Estimate
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Property Value</label>
                <div className="text-2xl font-bold text-green-400">
                  ${Number(propertyDetails.estimatedValue || 0).toLocaleString()}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Max Loan Amount (80% LTV)</label>
                <div className="text-xl font-bold text-gray-100">
                  ${(Number(propertyDetails.estimatedValue || 0) * 0.8).toLocaleString()}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-700/50">
                <div className="text-sm text-gray-400 mb-2">Estimated APR Range</div>
                <div className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-green-400" />
                  <span className="text-lg font-bold text-gray-100">6.5% - 12.5%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Final terms are determined after verification.</p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-gray-100 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Requirements
            </h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                <Shield className="w-4 h-4 text-green-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-100">Property Ownership</div>
                  <div className="text-xs text-gray-500">Clear title or significant equity required</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                <UserCheck className="w-4 h-4 text-blue-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-100">Income Verification</div>
                  <div className="text-xs text-gray-500">Stable income source with documentation</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                <Lock className="w-4 h-4 text-purple-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-100">Secure Process</div>
                  <div className="text-xs text-gray-500">Bank-level encryption & data protection</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reset */}
          <Button onClick={handleReset} disabled={isBusy} className="w-full border-gray-700 bg-gray-800/50 hover:bg-gray-800 text-gray-300">
            <Clock className="w-4 h-4 mr-2" />
            Start Over
          </Button>
        </div>
      </div>

      {/* Success Modal */}
      <Modal open={open} onClose={() => setOpen(false)} size="md">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>

          <h3 className="text-xl font-bold text-gray-100 mb-2">Application Submitted</h3>
          <p className="text-gray-400 mb-4">Your loan application has been received successfully.</p>

          <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
            <div className="text-sm text-gray-400 mb-1">Application ID</div>
            <div className="text-lg font-mono font-bold text-green-400">{applicationId}</div>
            <p className="text-xs text-gray-500 mt-2">Save this ID for future reference.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-400">
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Verification: 1–2 business days</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span>Decision: 3–5 business days</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span>Funding: 24 hours after approval</span>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button onClick={() => setOpen(false)} className="flex-1 border-gray-700 bg-gray-800 hover:bg-gray-700">
              Close
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                handleReset();
              }}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 border-transparent"
            >
              New Application
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
